import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/UIKit";
import { fmtNOK } from "@/lib/format";
import { Download, Landmark, Loader2, FileText } from "lucide-react";

export const PaymentAgreementsCard = ({ customer }) => {
  const init = customer.payment_agreements || {};
  const [f, setF] = useState({ avtalegiro_status: init.avtalegiro_status || "ingen", avtalegiro_limit_nok: init.avtalegiro_limit_nok ?? "", efaktura_status: init.efaktura_status || "ingen", efaktura_reference: init.efaktura_reference || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/customers/${customer.id}/payment-agreements`, { ...f, avtalegiro_limit_nok: f.avtalegiro_limit_nok === "" ? null : Number(f.avtalegiro_limit_nok), efaktura_reference: f.efaktura_reference || null });
      toast.success("Betalingsavtaler lagret");
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setBusy(false); }
  };
  const sel = "h-8 px-2 rounded-md bg-zinc-950 border border-zinc-800 text-xs text-zinc-200";
  return (
    <Card className="p-4" testid="payment-agreements-card">
      <div className="text-sm font-medium text-zinc-100 mb-3">Betalingsavtaler</div>
      <div className="space-y-2 text-xs">
        <div className="flex items-center justify-between gap-2">
          <span className="text-zinc-400">AvtaleGiro</span>
          <div className="flex gap-2">
            <input type="number" placeholder="Beløpsgrense kr" value={f.avtalegiro_limit_nok} onChange={(e) => setF({ ...f, avtalegiro_limit_nok: e.target.value })} className={`${sel} w-32`} data-testid="ag-limit" />
            <select value={f.avtalegiro_status} onChange={(e) => setF({ ...f, avtalegiro_status: e.target.value })} className={sel} data-testid="ag-status">
              <option value="ingen">Ingen</option><option value="aktiv">Aktiv</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-zinc-400">eFaktura</span>
          <div className="flex gap-2">
            <input placeholder="eFaktura-referanse" value={f.efaktura_reference} onChange={(e) => setF({ ...f, efaktura_reference: e.target.value })} className={`${sel} w-32`} data-testid="ef-ref" />
            <select value={f.efaktura_status} onChange={(e) => setF({ ...f, efaktura_status: e.target.value })} className={sel} data-testid="ef-status">
              <option value="ingen">Ingen</option><option value="aktiv">Aktiv</option>
            </select>
          </div>
        </div>
      </div>
      <button onClick={save} disabled={busy} className="mt-3 h-8 px-3 rounded-md border border-zinc-800 hover:bg-zinc-900 text-xs text-zinc-200 flex items-center gap-1.5" data-testid="agreements-save">
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Lagre
      </button>
    </Card>
  );
};

export const EFakturaCard = () => {
  const [p, setP] = useState(null);
  const load = () => api.get("/payments/efaktura/preview").then(({ data }) => setP(data)).catch(() => setP(null));
  useEffect(() => { load(); }, []);
  const download = async () => {
    try {
      const res = await api.get("/payments/efaktura/batch", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "efaktura.zip"; a.click(); URL.revokeObjectURL(url);
      const skipped = Number(res.headers["x-skipped"] || 0);
      toast.success(`eFaktura-fil laget${skipped ? ` – ${skipped} hoppet over (mangler referanse)` : ""}`);
      load();
    } catch (e) {
      const txt = e?.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : e?.response?.data?.detail;
      toast.error(txt || "Kunne ikke lage fil");
    }
  };
  if (!p) return null;
  return (
    <Card className="p-5 mb-6" testid="efaktura-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-emerald-400" /> eFaktura til nettbank</div>
          <div className="text-zinc-100 mt-1" data-testid="efaktura-summary">{p.count} nye fakturaer · {fmtNOK(p.total_nok)} · {p.customers} kunder med eFaktura</div>
          {p.missing_reference > 0 && <div className="text-xs text-amber-400 mt-1">{p.missing_reference} mangler eFaktura-referanse på kundekortet.</div>}
          {!p.configured && <div className="text-xs text-amber-400 mt-1">Legg inn eFaktura-oppsett (kontonummer) under Integrasjoner først.</div>}
          <div className="text-[11px] text-zinc-500 mt-1">Last opp ZIP-filen i bankens filmottak eller til Nets.</div>
        </div>
        <button onClick={download} disabled={!p.count || !p.configured} data-testid="efaktura-download"
          className="h-9 px-3 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black text-sm font-medium flex items-center gap-1.5">
          <Download className="h-4 w-4" /> Lag eFaktura-fil
        </button>
      </div>
    </Card>
  );
};

export const AvtaleGiroCard = () => {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/payments/avtalegiro/preview").then(({ data }) => setP(data)).catch(() => setP(null)); }, []);
  const download = async () => {
    try {
      const { data } = await api.get("/payments/avtalegiro/file", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = "avtalegiro.txt"; a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      const txt = e?.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : e?.response?.data?.detail;
      toast.error(txt || "Kunne ikke lage fil");
    }
  };
  if (!p) return null;
  return (
    <Card className="p-5 mb-6" testid="avtalegiro-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5"><Landmark className="h-3.5 w-3.5 text-emerald-400" /> AvtaleGiro-trekk</div>
          <div className="text-zinc-100 mt-1" data-testid="avtalegiro-summary">{p.count} fakturaer · {fmtNOK(p.total_nok)}</div>
          {!p.configured && <div className="text-xs text-amber-400 mt-1">Legg inn AvtaleGiro-oppsett under Integrasjoner først.</div>}
        </div>
        <button onClick={download} disabled={!p.count || !p.configured} data-testid="avtalegiro-download"
          className="h-9 px-3 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black text-sm font-medium flex items-center gap-1.5">
          <Download className="h-4 w-4" /> Last ned trekkfil til Nets
        </button>
      </div>
    </Card>
  );
};
